/**
 * doc/12 §3.1.4 — admin invite ledger.
 *
 * Single tab in AdminShell. Mint batches, watch redemptions (with
 * creator → redeemer emails so the propagation tree is readable),
 * revoke leaked codes, and service the waitlist. Code *sending* is
 * human-mediated for the friends-circle launch — this page only
 * tracks state.
 */
import { useEffect, useState } from 'react';
import { api } from '../api';

type Invites = Awaited<ReturnType<typeof api.adminListInvites>>['invites'];
type Waitlist = Awaited<ReturnType<typeof api.adminListWaitlist>>['waitlist'];

export function AdminInvites() {
  const [invites, setInvites] = useState<Invites | null>(null);
  const [waitlist, setWaitlist] = useState<Waitlist | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [genCount, setGenCount] = useState(30);
  const [genLabel, setGenLabel] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [freshCodes, setFreshCodes] = useState<string[] | null>(null);

  const reload = () => {
    api.adminListInvites().then((d) => setInvites(d.invites)).catch((e) =>
      setErr(e instanceof Error ? e.message : String(e)),
    );
    api.adminListWaitlist().then((d) => setWaitlist(d.waitlist)).catch(() => {});
  };
  useEffect(reload, []);

  const onGenerate = async () => {
    const label = genLabel.trim();
    if (!label || genBusy) return;
    setGenBusy(true);
    try {
      const res = await api.adminGenerateInvites(genCount, label);
      setFreshCodes(res.codes);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setGenBusy(false);
    }
  };

  const onRevoke = async (code: string) => {
    await api.adminRevokeInvite(code).catch(() => {});
    reload();
  };

  const onMarkInvited = async (email: string) => {
    await api.adminMarkWaitlistInvited(email).catch(() => {});
    reload();
  };

  if (err) {
    return (
      <div style={{ color: 'var(--red, #d23f3f)', fontSize: 13 }}>
        Failed to load invites: {err}
      </div>
    );
  }
  if (!invites) {
    return <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>;
  }

  const unused = invites.filter((i) => !i.used_at && !i.revoked).length;
  const redeemed = invites.filter((i) => i.used_at).length;
  const fmtDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: '2-digit',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const cellStyle: React.CSSProperties = {
    padding: '7px 10px',
    borderBottom: '1px solid var(--rule)',
    textAlign: 'left',
    verticalAlign: 'top',
  };

  return (
    <div style={{ fontSize: 13.5, color: 'var(--ink-1)' }}>
      {/* Mint a batch */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          marginBottom: 10,
          flexWrap: 'wrap',
        }}
      >
        <input
          type="number"
          min={1}
          max={200}
          value={genCount}
          onChange={(e) => setGenCount(Number(e.target.value) || 1)}
          style={{
            width: 70,
            padding: '7px 9px',
            border: '1px solid var(--rule)',
            borderRadius: 6,
            fontSize: 13,
            fontFamily: 'inherit',
          }}
        />
        <input
          type="text"
          placeholder="batch label (e.g. founder-batch-1)"
          value={genLabel}
          onChange={(e) => setGenLabel(e.target.value)}
          style={{
            flex: 1,
            minWidth: 200,
            padding: '7px 9px',
            border: '1px solid var(--rule)',
            borderRadius: 6,
            fontSize: 13,
            fontFamily: 'inherit',
          }}
        />
        <button
          type="button"
          disabled={!genLabel.trim() || genBusy}
          onClick={onGenerate}
          style={{
            padding: '7px 14px',
            background: 'var(--ink-0)',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            fontSize: 13,
            fontFamily: 'inherit',
            cursor: !genLabel.trim() || genBusy ? 'default' : 'pointer',
            opacity: !genLabel.trim() || genBusy ? 0.55 : 1,
          }}
        >
          {genBusy ? 'Generating…' : 'Generate'}
        </button>
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          {unused} unused · {redeemed} redeemed · {invites.length} total
        </span>
      </div>

      {/* Fresh batch — one-time copy surface (codes stay in the table
          below, this block just makes bulk copy painless right after
          minting). */}
      {freshCodes && (
        <div
          style={{
            marginBottom: 18,
            padding: '10px 12px',
            background: 'var(--paper-1)',
            border: '1px solid var(--rule)',
            borderRadius: 6,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              Fresh batch — {freshCodes.length} codes
            </span>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(freshCodes.join('\n')).catch(() => {});
              }}
              style={{
                fontSize: 11.5,
                padding: '3px 9px',
                border: '1px solid var(--rule)',
                borderRadius: 5,
                background: 'white',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              Copy all
            </button>
            <button
              type="button"
              onClick={() => setFreshCodes(null)}
              style={{
                fontSize: 11.5,
                padding: '3px 9px',
                border: 'none',
                background: 'transparent',
                fontFamily: 'inherit',
                color: 'var(--ink-3)',
                cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
          </div>
          <div className="mono" style={{ fontSize: 12.5, lineHeight: 1.7, letterSpacing: '.06em' }}>
            {freshCodes.join('  ')}
          </div>
        </div>
      )}

      {/* Ledger */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 28 }}>
        <thead>
          <tr style={{ fontSize: 11.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            <th style={cellStyle}>Code</th>
            <th style={cellStyle}>Batch</th>
            <th style={cellStyle}>Created</th>
            <th style={cellStyle}>Creator</th>
            <th style={cellStyle}>Redeemed by</th>
            <th style={cellStyle}>Status</th>
            <th style={cellStyle} />
          </tr>
        </thead>
        <tbody>
          {invites.map((i) => (
            <tr key={i.code}>
              <td style={{ ...cellStyle, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '.08em' }}>
                {i.code}
              </td>
              <td style={cellStyle}>{i.batch_label}</td>
              <td style={cellStyle}>{fmtDate(i.created_at)}</td>
              <td style={cellStyle}>{i.creator_email ?? 'admin'}</td>
              <td style={cellStyle}>{i.redeemer_email ?? '—'}</td>
              <td style={cellStyle}>
                {i.revoked ? 'revoked' : i.used_at ? `used ${fmtDate(i.used_at)}` : 'unused'}
              </td>
              <td style={cellStyle}>
                {!i.used_at && !i.revoked && (
                  <button
                    type="button"
                    onClick={() => onRevoke(i.code)}
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      border: '1px solid var(--rule)',
                      borderRadius: 5,
                      background: 'white',
                      fontFamily: 'inherit',
                      color: 'var(--ink-2)',
                      cursor: 'pointer',
                    }}
                  >
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Waitlist */}
      <div style={{ fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
        Waitlist {waitlist ? `(${waitlist.length})` : ''}
      </div>
      {waitlist && waitlist.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ fontSize: 11.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
              <th style={cellStyle}>Email</th>
              <th style={cellStyle}>Locale</th>
              <th style={cellStyle}>Joined</th>
              <th style={cellStyle}>Invited</th>
              <th style={cellStyle} />
            </tr>
          </thead>
          <tbody>
            {waitlist.map((w) => (
              <tr key={w.email}>
                <td style={cellStyle}>{w.email}</td>
                <td style={cellStyle}>{w.locale ?? '—'}</td>
                <td style={cellStyle}>{fmtDate(w.created_at)}</td>
                <td style={cellStyle}>{w.invited_at ? fmtDate(w.invited_at) : '—'}</td>
                <td style={cellStyle}>
                  {!w.invited_at && (
                    <button
                      type="button"
                      onClick={() => onMarkInvited(w.email)}
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        border: '1px solid var(--rule)',
                        borderRadius: 5,
                        background: 'white',
                        fontFamily: 'inherit',
                        color: 'var(--ink-2)',
                        cursor: 'pointer',
                      }}
                    >
                      Mark invited
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No one waiting.</div>
      )}
    </div>
  );
}
